import { createContext, useContext, useEffect, useState } from 'react';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const getSystemTheme = () =>
	window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';

const initialState = {
	theme: 'system',
	resolvedTheme: 'light',
	setTheme: () => null,
};

const ThemeProviderContext = createContext(initialState);

export function ThemeProvider({
	children,
	defaultTheme = 'system',
	storageKey = 'ui-theme',
	...props
}) {
	const [theme, setTheme] = useState(
		() => localStorage.getItem(storageKey) || defaultTheme
	);
	const [systemTheme, setSystemTheme] = useState(getSystemTheme);

	useEffect(() => {
		const media = window.matchMedia(DARK_QUERY);
		const onChange = () => setSystemTheme(media.matches ? 'dark' : 'light');
		media.addEventListener('change', onChange);
		return () => media.removeEventListener('change', onChange);
	}, []);

	const resolvedTheme = theme === 'system' ? systemTheme : theme;

	useEffect(() => {
		const root = window.document.documentElement;
		root.classList.remove('light', 'dark');
		root.classList.add(resolvedTheme);
		root.style.colorScheme = resolvedTheme;
		// Tints the iOS/Android browser bars to match the app background.
		document
			.querySelector('meta[name="theme-color"]')
			?.setAttribute('content', resolvedTheme === 'dark' ? '#0c0a09' : '#ffffff');
	}, [resolvedTheme]);

	const value = {
		theme,
		// The theme actually on screen ('light' | 'dark'); use this for styling
		// decisions, since `theme` can be 'system'.
		resolvedTheme,
		setTheme: (theme) => {
			localStorage.setItem(storageKey, theme);
			setTheme(theme);
		},
	};

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useTheme = () => {
	const context = useContext(ThemeProviderContext);

	if (context === undefined)
		throw new Error('useTheme must be used within a ThemeProvider');

	return context;
};

export { ThemeProviderContext };
